import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  Signal,
  computed,
  contentChild,
  contentChildren,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { AvatarComponent } from '@softline/ui-core';
import { translator } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { NumberBadgeComponent } from '@softline/ui-core';
import { NumberBadgeVariant } from '@softline/ui-core';
import {
  TimelineDensity,
  TimelineItemState,
  TimelineNodeVariant,
} from './timeline.types';

type TimelineContext = {
  density: Signal<TimelineDensity>;
  nodeVariant: Signal<TimelineNodeVariant>;
  itemIndex: (item: unknown) => number;
  isFirst: (item: unknown) => boolean;
  isLast: (item: unknown) => boolean;
  previousItemState: (item: unknown) => TimelineItemState | null;
  lastItemState: () => TimelineItemState | null;
};

const TIMELINE_CONTEXT = new InjectionToken<TimelineContext>('TIMELINE_CONTEXT');

@Component({
  selector: 'soft-timeline-title, soft-ds-timeline-title',
  template: '<ng-content />',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'soft-timeline-title ds-body-2-semibold',
  },
})
export class TimelineTitleComponent {}

@Component({
  selector: 'soft-timeline-description, soft-ds-timeline-description',
  template: '<ng-content />',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'soft-timeline-description ds-body-2',
  },
})
export class TimelineDescriptionComponent {}

@Component({
  selector: 'soft-timeline-meta, soft-ds-timeline-meta',
  template: '<ng-content />',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'soft-timeline-meta ds-caption',
  },
})
export class TimelineMetaComponent {}

@Component({
  selector: 'soft-timeline-node, soft-ds-timeline-node',
  template: '<ng-content />',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'soft-timeline-custom-node',
  },
})
export class TimelineNodeComponent {}

@Component({
  selector: 'soft-timeline-continuation, soft-ds-timeline-continuation',
  template: `
    <div class="soft-timeline-continuation">
      <div class="soft-timeline-continuation-rail" aria-hidden="true">
        <span class="soft-timeline-connector soft-timeline-connector-before"></span>
      </div>
      <div class="soft-timeline-continuation-content">
        <ng-content />
      </div>
    </div>
  `,
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': "'listitem'",
  },
})
export class TimelineContinuationComponent {
  private readonly timeline = inject(TIMELINE_CONTEXT, { optional: true });

  protected readonly hostClasses = computed(() => {
    const density = this.timeline?.density() ?? 'comfortable';
    const connectorTone = this.connectorToneForState(
      this.timeline?.lastItemState() ?? null
    );

    return [
      'soft-timeline-continuation-host',
      `density-${density}`,
      `connector-before-${connectorTone}`,
    ].join(' ');
  });

  private connectorToneForState(state: TimelineItemState | null): string {
    switch (state) {
      case 'completed':
      case 'success':
        return 'success';
      case 'current':
        return 'current';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      default:
        return 'neutral';
    }
  }
}

@Component({
  selector: 'soft-timeline-item, soft-ds-timeline-item',
  imports: [AvatarComponent, IconComponent, NumberBadgeComponent],
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': "'listitem'",
    '[attr.aria-current]': 'state() === "current" ? "step" : null',
    '[attr.aria-disabled]': 'state() === "disabled" ? "true" : null',
  },
})
export class TimelineItemComponent {
  readonly state = input<TimelineItemState>('default');
  readonly icon = input<string | null>(null);
  readonly avatarSrc = input<string | null>(null);
  readonly avatarAlt = input<string | null>(null);
  readonly avatarInitials = input<string | null>(null);
  readonly identityName = input<string | null>(null);

  private readonly timeline = inject(TIMELINE_CONTEXT, { optional: true });
  private readonly customNode = contentChild(TimelineNodeComponent);
  private readonly meta = contentChild(TimelineMetaComponent);

  protected readonly index = computed(() => this.timeline?.itemIndex(this) ?? 0);
  protected readonly nodeVariant = computed(() => this.timeline?.nodeVariant() ?? 'dot');
  protected readonly stateIcon = computed(() => this.iconForState(this.state()));
  protected readonly resolvedIcon = computed(() => this.icon() || this.stateIcon());
  protected readonly hasCustomNode = computed(() => !!this.customNode());
  protected readonly hasAvatar = computed(
    () => !!this.avatarSrc() || !!this.avatarInitials()
  );
  protected readonly hasMeta = computed(() => !!this.meta());
  protected readonly hasIdentity = computed(() => !!this.identityName());
  protected readonly hasMetaRow = computed(() => this.hasMeta() || this.hasIdentity());
  protected readonly connectorBeforeTone = computed(() =>
    this.connectorToneForState(this.timeline?.previousItemState(this) ?? null)
  );
  protected readonly rendersNumber = computed(
    () =>
      !this.hasCustomNode() &&
      !this.hasAvatar() &&
      !this.resolvedIcon() &&
      this.nodeVariant() === 'number'
  );
  protected readonly numberBadgeVariant = computed<NumberBadgeVariant>(() =>
    this.state() === 'current' ? 'info' : 'secondary'
  );

  protected readonly hostClasses = computed(() => {
    const state = this.state();
    const density = this.timeline?.density() ?? 'comfortable';
    return [
      'soft-timeline-item-host',
      `state-${state}`,
      `density-${density}`,
      `connector-before-${this.connectorBeforeTone()}`,
      this.timeline?.isFirst(this) ? 'is-first' : '',
      this.timeline?.isLast(this) ? 'is-last' : '',
      this.hasCustomNode() ? 'has-custom-node' : '',
      this.hasAvatar() ? 'has-avatar-node' : '',
      this.rendersNumber() ? 'has-number-node' : '',
    ]
      .filter(Boolean)
      .join(' ');
  });

  private iconForState(state: TimelineItemState): string | null {
    switch (state) {
      case 'completed':
      case 'success':
        return 'fa-solid fa-check';
      case 'warning':
      case 'danger':
        return 'fa-solid fa-triangle-exclamation';
      default:
        return null;
    }
  }

  private connectorToneForState(state: TimelineItemState | null): string {
    switch (state) {
      case 'completed':
      case 'success':
        return 'success';
      case 'current':
        return 'current';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      default:
        return 'neutral';
    }
  }
}

@Component({
  selector: 'soft-timeline, soft-ds-timeline',
  template: '<ng-content />',
  styleUrl: './timeline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TIMELINE_CONTEXT,
      useExisting: forwardRef(() => TimelineComponent),
    },
  ],
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': '"list"',
    '[attr.aria-label]': 'resolvedAriaLabel()',
  },
})
export class TimelineComponent implements TimelineContext {
  readonly density = input<TimelineDensity>('comfortable');
  readonly nodeVariant = input<TimelineNodeVariant>('dot');
  readonly ariaLabel = input<string | null>('#UI_CORE.COMPONENTS.TIMELINE.ARIA_LABEL', {
    alias: 'aria-label',
  });

  private readonly t = translator();
  private readonly defaultAriaLabel = this.t('#UI_CORE.COMPONENTS.TIMELINE.ARIA_LABEL');

  private readonly items = contentChildren(TimelineItemComponent);
  private readonly continuation = contentChild(TimelineContinuationComponent);

  protected readonly hostClasses = computed(
    () =>
      `soft-timeline-host density-${this.density()} node-variant-${this.nodeVariant()}`
  );

  protected readonly resolvedAriaLabel = computed(() => {
    const ariaLabel = this.ariaLabel();
    if (ariaLabel === '#UI_CORE.COMPONENTS.TIMELINE.ARIA_LABEL') {
      return this.defaultAriaLabel();
    }
    return ariaLabel;
  });

  itemIndex(item: unknown): number {
    return this.items().indexOf(item as TimelineItemComponent);
  }

  isFirst(item: unknown): boolean {
    return this.itemIndex(item) === 0;
  }

  isLast(item: unknown): boolean {
    return this.itemIndex(item) === this.items().length - 1 && !this.continuation();
  }

  previousItemState(item: unknown): TimelineItemState | null {
    const index = this.itemIndex(item);
    if (index <= 0) {
      return null;
    }

    return this.items()[index - 1]?.state() ?? null;
  }

  lastItemState(): TimelineItemState | null {
    return this.items().at(-1)?.state() ?? null;
  }
}
